/**
 * analytics.js — PostHog wrapper with graceful degradation.
 *
 * PostHog is loaded via a snippet in public/index.html (window.posthog).
 * This module hides the noisy `if (window.posthog)` checks from consumers
 * and adds shared properties (platform, auth state) so every event is
 * enriched consistently.
 *
 * Zero throw guarantee — analytics failures NEVER break user-facing UX
 * (adblockers, dev mode, network hiccups all silently no-op).
 */

const isBrowser = () => typeof window !== 'undefined';

/**
 * GDPR / loi camerounaise — analytics ne s'exécute que si l'utilisateur
 * a explicitement consenti à la catégorie "analytics" via le bandeau
 * CookieConsent. Refus par défaut si aucun choix stocké.
 */
const analyticsAllowed = () => {
  if (!isBrowser()) return false;
  try {
    const raw = localStorage.getItem('realty237.cookie-consent');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.prefs?.analytics;
  } catch {
    return false;
  }
};

/**
 * Detect the delivery platform for a PWA/analytics event.
 * Returns "ios" | "android" | "desktop" | "unknown".
 */
export const detectPlatform = () => {
  if (!isBrowser()) return 'unknown';
  const ua = (window.navigator?.userAgent || '').toLowerCase();
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true;
  if (/iphone|ipad|ipod/.test(ua)) return isStandalone ? 'ios-standalone' : 'ios';
  if (/android/.test(ua)) return isStandalone ? 'android-standalone' : 'android';
  if (window.innerWidth < 768) return 'mobile';
  return 'desktop';
};

/**
 * Return the current user's auth state without importing the auth module
 * (kept dependency-free so analytics.js is trivially unit-testable).
 */
const detectAuthState = () => {
  if (!isBrowser()) return { is_authenticated: false, role: 'anonymous' };
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return { is_authenticated: false, role: 'anonymous' };
    const u = JSON.parse(raw);
    return {
      is_authenticated: true,
      role: u?.role || 'user',
      // Never log PII (email/name/phone). Just an anonymised id if present.
      user_hash: u?.id ? String(u.id).slice(0, 8) : undefined,
    };
  } catch {
    return { is_authenticated: false, role: 'anonymous' };
  }
};

/**
 * Send an event to PostHog. Enriches with platform + auth context so the
 * dashboard can slice by device / user cohort without extra work.
 *
 * @param {string} eventName — snake_case per PostHog convention
 * @param {object} [props]   — additional properties merged on top
 */
export const trackEvent = (eventName, props = {}) => {
  if (!isBrowser() || !window.posthog) return;
  // GDPR gate — no capture without explicit analytics consent.
  if (!analyticsAllowed()) return;
  try {
    window.posthog.capture(eventName, {
      platform: detectPlatform(),
      viewport_width: window.innerWidth,
      ...detectAuthState(),
      ...props,
    });
  } catch {
    /* Never throw from analytics */
  }
};

/**
 * Convenience helpers for the 3 PWA lifecycle events audited in the PRD.
 * Keeping them typed as separate functions makes the call sites more readable
 * and prevents typos in the event name across components.
 */
export const trackPwaPromptShown = (props = {}) =>
  trackEvent('pwa_prompt_shown', { source: 'install_banner', ...props });

export const trackPwaPromptInstalled = (props = {}) =>
  trackEvent('pwa_prompt_installed', { source: 'install_banner', ...props });

export const trackPwaPromptDismissed = (dismissMethod = 'close_button', props = {}) =>
  trackEvent('pwa_prompt_dismissed', {
    source: 'install_banner',
    dismiss_method: dismissMethod,
    ...props,
  });
